/**
 * A unit, as a card.
 *
 * **The picture is the point.** A table of thirty units is thirty rows of the same shape, and the
 * thing an agent is actually looking for — which flat is this, is it empty, what does it fetch —
 * is spread across seven columns of text. A card leads with the photograph the listing leads with,
 * and everything else hangs off it.
 *
 * **Every unit has a picture, whether or not it has a photograph.** A unit with none gets the
 * drawing of its property's type: the same artwork the public site uses, exported by
 * `npm run brand` and served from `public/illustrations/`. A grid of grey boxes reads as a broken
 * page; a grid of drawings reads as a set of buildings somebody has not photographed yet.
 *
 * The same card is used by the units screen and by a property's own list. `showProperty` is the
 * only difference between them: on a property's page every card would say the same building name.
 */

import { Link } from "react-router-dom";
import { diffDays, termSuffix, toTerm, type RentCycle, type UnitStatus } from "@rentorng/core";
import { useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { useRecordLock } from "@/lib/gating";
import { Padlock } from "@/components/padlock";
import { UnitStatusBadge } from "@/components/status";
import { OfferTags } from "@/components/offer-icons";
import { IconExternal, IconEye, IconEyeOff } from "@/components/icons";
import { PinButton } from "@/components/pin-button";

export interface UnitCardRow {
  id: string;
  label: string;
  status: string;
  bedrooms: number | null;
  bathrooms?: number | null;
  toilets?: number | null;
  sizeSqm?: number | null;
  listed?: boolean | null;
  /** `approved` unless a review policy is holding this listing back. See ADR-0078. */
  reviewState?: "approved" | "pending" | "refused" | null;
  offers: string[] | null;
  rentAmount: number | null;
  rentCycle: string;
  rentCycleEvery?: number;
  vacantSince: string | null;
  propertyId: string;
  propertyName: string;
  propertyTypes: string[];
  ownerId: string | null;
  ownerName: string | null;
  ownerIsOwnLandlord?: boolean;
  tenantName: string | null;
  leaseId: string | null;
  leaseEnd: string | null;
  /** The first photograph, if the unit has one. The bytes come from the API, never from a path. */
  photoId?: string | null;
  /** When it was pinned to the top of the list, or null. */
  pinnedAt?: string | null;
  /**
   * Where this unit is on the public site, or null when it is not there at all.
   *
   * The server decides: the market shows what is listed *and* vacant, so a unit that is either
   * private or occupied has no page to open and no button offering to open one.
   */
  publicSlug?: string | null;
}

/** The drawing for a type, from the set the public site is illustrated with. */
const illustrationFor = (types: string[] | null | undefined) =>
  `/illustrations/${types?.[0] ?? "block_of_flats"}.svg`;

export function UnitCard({
  unit,
  showProperty = true,
  today = "",
}: {
  unit: UnitCardRow;
  showProperty?: boolean;
  today?: string;
}) {
  const fmt = useFormat();
  const { vocab, mayReach, listingsUrl } = useSession();

  /*
    Whether this one is above what the plan opens.

    Asked here rather than passed in, so the two screens that draw this card — the units list and a
    property's own page — cannot end up disagreeing about which units are padlocked. The answer is
    the server's, read through `useRecordLock`, which also settles the permission question: a role
    that could not edit a unit anyway gets no padlock, because paying would not change anything for
    them and a padlock nobody can open teaches people to ignore the ones that can be.
  */
  const lock = useRecordLock("units");
  const locked = lock.isLocked(unit.id);

  /*
    Where the card goes.

    A unit has no read-only page of its own — the form is the page — so for somebody who cannot
    edit one there is nothing at the other end of that link but a redirect. They get the building
    instead, which is a screen they can actually use.
  */
  /* Where the card goes, which is a question of reach rather than of writing — a read-only
     agency still needs to open a unit and look at it. */
  const href = mayReach("property.edit") ? `/app/units/${unit.id}/edit` : `/app/properties/${unit.propertyId}`;

  const facts = [
    unit.bedrooms ? `${unit.bedrooms} bed` : null,
    unit.bathrooms ? `${unit.bathrooms} bath` : null,
    unit.toilets ? `${unit.toilets} toilet${unit.toilets === 1 ? "" : "s"}` : null,
    unit.sizeSqm ? `${unit.sizeSqm} sqm` : null,
  ].filter(Boolean);

  const empty =
    unit.status === "vacant" && unit.vacantSince && today ? diffDays(unit.vacantSince, today) : null;

  return (
    <article className="card group relative flex h-full flex-col overflow-hidden transition-shadow hover:shadow-md">
      <Link to={href} className="relative block bg-ink-50" aria-label={unit.label}>
        <img
          src={unit.photoId ? `/api/v1/public/media/${unit.photoId}` : illustrationFor(unit.propertyTypes)}
          alt=""
          loading="lazy"
          className="aspect-[4/3] w-full object-cover"
        />

        {/* Beside the status, which is the other thing about a unit you read before its name. The
            picture, the rent and the tenancy stay exactly as they were: the padlock says the
            unit's own screen is shut, not that anything has been taken away. */}
        <span className="absolute left-2 top-2 flex flex-wrap gap-1.5">
          <UnitStatusBadge status={unit.status as UnitStatus} />
          {locked ? <Padlock gate={lock.gate} label="Locked" className="bg-white/95" /> : null}
        </span>

        {/*
          Whether the outside world can see it, on the picture, because that is the one thing about
          a unit that is invisible everywhere else until somebody opens the form.

          **Three states, not two.** A listing held for review is switched on and still not public,
          and drawing that as "Listed" would be the one badge on this card that is a lie. It is
          amber rather than green for the same reason: it is not finished.
        */}
        {(() => {
          const held = unit.listed && unit.reviewState && unit.reviewState !== "approved";
          const label = !unit.listed ? "Private" : held ? (unit.reviewState === "refused" ? "Not approved" : "In review") : "Listed";
          const tone = !unit.listed
            ? "bg-white/90 text-ink-500"
            : held
              ? "bg-warn-500 text-white"
              : "bg-brand-600 text-white";
          const title = !unit.listed
            ? "Not shown publicly"
            : held
              ? unit.reviewState === "refused"
                ? "Not approved — open the unit to see why"
                : "Waiting to be checked before it appears publicly"
              : "Shown on the public site while vacant";
          return (
            <span
              className={`absolute right-2 top-2 flex items-center gap-1 rounded-[var(--radius-pill)] px-2 py-1 text-[11px] font-semibold ${tone}`}
              title={title}
            >
              {unit.listed && !held ? <IconEye size={13} /> : <IconEyeOff size={13} />}
              {label}
            </span>
          );
        })()}
      </Link>

      {/*
        The pin, on the picture rather than in the body.

        It is a state of the card and not a fact about the unit, so it belongs where the other
        state already is. It sits outside the Link on purpose — nested inside, a press on it would
        open the unit it was meant to mark.
      */}
      <div className="absolute left-2 bottom-2 z-10">
        <PinButton
          kind="unit"
          id={unit.id}
          pinnedAt={unit.pinnedAt}
          invalidates={[["units"], ["property", unit.propertyId]]}
        />
      </div>


      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Link to={href} className="link block truncate font-semibold">
              {unit.label}
            </Link>
            {showProperty ? (
              <Link
                to={`/app/properties/${unit.propertyId}`}
                className="block truncate text-sm text-ink-500 hover:underline"
              >
                {unit.propertyName}
              </Link>
            ) : null}
          </div>

          {unit.rentAmount ? (
            <div className="shrink-0 text-right">
              <span className="tabular block font-semibold text-ink-900">{fmt.money(unit.rentAmount)}</span>
              <span className="block text-[11px] text-ink-400">
                {termSuffix(toTerm(unit.rentCycle as RentCycle, unit.rentCycleEvery))}
              </span>
            </div>
          ) : null}
        </div>

        {facts.length ? <p className="text-sm text-ink-600">{facts.join(" · ")}</p> : null}

        {unit.offers && !(unit.offers.length === 1 && unit.offers[0] === "rent") ? (
          <OfferTags offers={unit.offers} />
        ) : null}

        {/*
          The foot is whatever the unit's state makes worth knowing — who is in it and until when,
          or how long it has been standing empty — and, on the right, the way to see what a stranger
          sees. The link was drawn over the picture at first, which put it at the top of the page
          instead: `absolute` needs a positioned ancestor and the card had never been one.
        */}
        <div className="mt-auto flex items-center justify-between gap-2 border-t border-ink-100 pt-2 text-sm">
          <span className="min-w-0 flex-1">
          {unit.tenantName ? (
            <p className="flex items-center justify-between gap-2 text-ink-600">
              <span className="truncate">{unit.tenantName}</span>
              {unit.leaseId ? (
                <Link to={`/app/tenancies/${unit.leaseId}`} className="shrink-0 text-xs text-ink-500 hover:underline">
                  to {fmt.date(unit.leaseEnd)}
                </Link>
              ) : null}
            </p>
          ) : empty !== null ? (
            <p className="text-danger-700">Empty for {empty} days</p>
          ) : unit.ownerId ? (
            <Link to={`/app/clients/${unit.ownerId}`} className="text-ink-500 hover:underline">
              {unit.ownerName}
            </Link>
          ) : (
            <p className="text-ink-400">No {vocab.tenant}</p>
          )}
          </span>

          {/*
            The public page, for the units that have one.

            An agency's first question about a listing is what a stranger sees, and the honest
            answer is the page itself. It leaves the application, so: a new tab, and `noreferrer`
            because nothing about this session should travel with it.
          */}
          {unit.publicSlug && listingsUrl ? (
            <a
              href={`${listingsUrl}/listing/${unit.publicSlug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex shrink-0 items-center gap-1 rounded-[var(--radius-control)] px-2 py-1 text-xs font-medium text-ink-500 hover:bg-ink-50 hover:text-brand-700"
              title="Open the public page for this unit"
            >
              <IconExternal size={13} />
              View on site
            </a>
          ) : null}
        </div>
      </div>
    </article>
  );
}

/** The grid the cards sit in. One place, so both screens breathe the same way. */
export function UnitGrid({
  units,
  showProperty = true,
  today = "",
}: {
  units: UnitCardRow[];
  showProperty?: boolean;
  today?: string;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {units.map((unit) => (
        <UnitCard key={unit.id} unit={unit} showProperty={showProperty} today={today} />
      ))}
    </div>
  );
}

/**
 * Grid or list.
 *
 * Grid is the default: with a photograph on every card it is the faster read, and it is what
 * anybody who has used a property site expects. The table is still there because thirty units with
 * their rents in a column is the faster read when what you are doing is checking rents — the two
 * views answer different questions, and the choice is kept in the address so a link carries it.
 */
export function ViewSwitch({ view, onChange }: { view: "grid" | "list"; onChange: (next: "grid" | "list") => void }) {
  return (
    <div className="inline-flex rounded-[var(--radius-control)] border border-ink-200 bg-white p-0.5" role="group" aria-label="Layout">
      {(["grid", "list"] as const).map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={view === option}
          onClick={() => onChange(option)}
          className={`rounded-[var(--radius-control)] px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
            view === option ? "bg-brand-600 text-white" : "text-ink-600 hover:bg-ink-50"
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  );
}
